import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { databasesApi } from '@/api/databases';
import { useBackupDownload } from '@/hooks/useBackupDownload';
import { DownloadProgressModal } from '@/components/backup/DownloadProgressModal';
import type { DatabaseSchool, Backup, BackupVersion } from '@/types/database';
import { DataTable, Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Database,
  Download,
  Plus,
  Trash2,
  Clock,
  Save,
  Loader2,
  Search,
  X,
} from 'lucide-react';

export function DatabasesPage() {
  const [schools, setSchools] = useState<DatabaseSchool[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<DatabaseSchool | null>(null);
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState<BackupVersion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteBackup, setDeleteBackup] = useState<Backup | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [schoolSearch, setSchoolSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const { downloadInfo, modalOpen, setModalOpen, download: startDownload, reset: resetDownload } = useBackupDownload();

  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    try {
      setLoadingSchools(true);
      setError(null);
      const response = await databasesApi.getSchools();
      setSchools(response.data);
    } catch (err) {
      setError('Erreur lors du chargement des écoles');
      console.error(err);
    } finally {
      setLoadingSchools(false);
    }
  };

  const fetchBackups = async (schoolCode: string) => {
    try {
      setLoadingBackups(true);
      setError(null);
      const data = await databasesApi.getBackups(schoolCode);
      setBackups(data);
    } catch (err) {
      setError('Erreur lors du chargement des sauvegardes');
      console.error(err);
    } finally {
      setLoadingBackups(false);
    }
  };

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (
        inputWrapperRef.current && !inputWrapperRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update dropdown position when showing
  useEffect(() => {
    if (showDropdown && inputWrapperRef.current) {
      const rect = inputWrapperRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, [showDropdown, schoolSearch]);

  const filteredSchools = useMemo(() => {
    if (!schoolSearch.trim()) return schools;
    const q = schoolSearch.toLowerCase();
    return schools.filter(
      (s) => s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q)
    );
  }, [schools, schoolSearch]);

  const handleSelectSchool = useCallback((school: DatabaseSchool) => {
    setSelectedSchool(school);
    setSchoolSearch(`${school.name} (${school.code})`);
    setShowDropdown(false);
    fetchBackups(school.code);
  }, []);

  const handleClearSchool = useCallback(() => {
    setSelectedSchool(null);
    setSchoolSearch('');
    setBackups([]);
  }, []);

  const handleCreateBackup = async (version: BackupVersion) => {
    if (!selectedSchool) return;

    try {
      setCreatingBackup(version);
      setError(null);
      await databasesApi.createBackup(selectedSchool.code, version);
      await fetchBackups(selectedSchool.code);
    } catch (err) {
      setError('Erreur lors de la création de la sauvegarde');
      console.error(err);
    } finally {
      setCreatingBackup(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteBackup || !selectedSchool) return;

    try {
      setDeleting(true);
      await databasesApi.deleteBackup(selectedSchool.code, deleteBackup.id);
      setBackups(backups.filter((b) => b.id !== deleteBackup.id));
      setDeleteBackup(null);
    } catch (err) {
      console.error(err);
      setError('Erreur lors de la suppression de la sauvegarde');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadBackup = (backup: Backup) => {
    if (!selectedSchool) return;
    const url = databasesApi.getBackupDownloadUrl(selectedSchool.code, backup.id);
    startDownload(url, backup.fileName);
  };

  const handleDownloadDevDump = () => {
    const url = databasesApi.getDevDumpUrl();
    const fileName = `dev_${new Date().toISOString().replace(/[:.]/g, '-')}.zip`;
    startDownload(url, fileName);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatSize = (bytes: number): string => {
    if (bytes === 0) return 'N/C';
    const k = 1024;
    const sizes = ['o', 'Ko', 'Mo', 'Go', 'To'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getBackupTypeIcon = (type: string) => {
    if (type === 'auto') {
      return <span title="Sauvegarde automatisee"><Clock className="h-4 w-4" /></span>;
    }
    return <span title="Sauvegarde manuelle"><Save className="h-4 w-4" /></span>;
  };

  const columns: Column<Backup>[] = [
    {
      key: 'type',
      header: '',
      className: 'w-12',
      render: (backup) => (
        <div className="flex justify-center text-muted-foreground">
          {getBackupTypeIcon(backup.type)}
        </div>
      ),
    },
    {
      key: 'version',
      header: 'Env',
      className: 'w-16',
      render: (backup) => (
        <Badge
          variant="outline"
          className={
            backup.version === 'v2'
              ? 'border-violet-300 bg-violet-50 text-violet-700'
              : 'border-sky-300 bg-sky-50 text-sky-700'
          }
        >
          {backup.version === 'v2' ? 'V2' : 'V1'}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: 'Date',
      className: 'w-44',
      render: (backup) => (
        <span className="text-sm">{formatDate(backup.createdAt)}</span>
      ),
    },
    {
      key: 'fileName',
      header: 'Fichier',
      render: (backup) => (
        <span className="font-mono text-sm">{backup.fileName}</span>
      ),
    },
    {
      key: 'size',
      header: 'Poids',
      className: 'w-24',
      render: (backup) => (
        <Badge variant="outline">{backup.sizeFormatted || formatSize(backup.size)}</Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right w-24',
      render: (backup) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => handleDownloadBackup(backup)}
            title="Telecharger"
          >
            <Download className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setDeleteBackup(backup)}
            title="Supprimer"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Database className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              Bases de donnees
            </h1>
          </div>
          <p className="text-sm md:text-base text-muted-foreground pl-[52px]">
            Gérez les sauvegardes des bases de données des écoles
          </p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Development dump section */}
      <div className="rounded-lg border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Base de developpement</h2>
        <Button onClick={handleDownloadDevDump} className="gap-2">
          <Download className="h-4 w-4" />
          Telecharger la base de developpement
        </Button>
      </div>

      {/* School selection and backups */}
      <div className="rounded-lg border bg-card p-6 overflow-visible">
        <h2 className="text-lg font-semibold mb-4">Sauvegardes par école</h2>

        {/* School selector - autocomplete */}
        <div className="mb-6">
          <Label htmlFor="school-search" className="mb-2 block">
            École en production
          </Label>
          <div className="relative w-full max-w-lg" ref={inputWrapperRef}>
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              id="school-search"
              value={schoolSearch}
              onChange={(e) => {
                setSchoolSearch(e.target.value);
                setShowDropdown(true);
                if (selectedSchool) {
                  setSelectedSchool(null);
                  setBackups([]);
                }
              }}
              onFocus={() => setShowDropdown(true)}
              placeholder="Rechercher par nom ou code..."
              disabled={loadingSchools}
              className="pl-9 pr-9"
            />
            {schoolSearch && (
              <button
                type="button"
                onClick={handleClearSchool}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            {showDropdown && !selectedSchool && dropdownPos && createPortal(
              <div
                ref={dropdownRef}
                className="fixed z-[9999] bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
                style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
              >
                {filteredSchools.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-muted-foreground">
                    Aucune école trouvée
                  </div>
                ) : (
                  filteredSchools.map((school) => (
                    <button
                      key={school.id}
                      type="button"
                      className="w-full text-left px-4 py-2.5 hover:bg-muted/50 transition-colors flex items-center justify-between gap-2"
                      onClick={() => handleSelectSchool(school)}
                    >
                      <span className="text-sm font-medium truncate">{school.name}</span>
                      <span className="text-xs text-muted-foreground font-mono flex-shrink-0">{school.code}</span>
                    </button>
                  ))
                )}
              </div>,
              document.body
            )}
          </div>
        </div>

        {/* Backups section - only show when school is selected */}
        {selectedSchool && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium">
                Sauvegardes de {selectedSchool.name}
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => handleCreateBackup('v1')}
                  disabled={creatingBackup !== null}
                  className="gap-2"
                >
                  {creatingBackup === 'v1' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  {creatingBackup === 'v1'
                    ? 'Création...'
                    : selectedSchool.hasV2
                      ? 'Sauvegarde V1'
                      : 'Créer une sauvegarde'}
                </Button>
                {selectedSchool.hasV2 && (
                  <Button
                    onClick={() => handleCreateBackup('v2')}
                    disabled={creatingBackup !== null}
                    variant="outline"
                    className="gap-2"
                  >
                    {creatingBackup === 'v2' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                    {creatingBackup === 'v2' ? 'Création...' : 'Sauvegarde V2'}
                  </Button>
                )}
              </div>
            </div>

            <DataTable
              columns={columns}
              data={backups}
              loading={loadingBackups}
              totalItems={backups.length}
              emptyMessage="Aucune sauvegarde trouvée pour cette école"
            />
          </>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteBackup} onOpenChange={() => setDeleteBackup(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la sauvegarde ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimer la sauvegarde{' '}
              <strong>{deleteBackup?.fileName}</strong> ?
              <br />
              <br />
              Cette action est irreversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DownloadProgressModal
        open={modalOpen}
        onOpenChange={(open) => { if (!open) resetDownload(); else setModalOpen(open); }}
        state={downloadInfo.state}
        progress={downloadInfo.progress}
        fileName={downloadInfo.fileName}
        fileSize={downloadInfo.fileSize}
        error={downloadInfo.error}
      />
    </div>
  );
}
